import React, { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../../AuthContextWrapper";
import { FinancialConvoContext } from "FinancialConvoContextWrapper";
import { LARAVEL_APP_HOME, LARAVEL_APP_HOST } from "../../../constants";
import { Box, Button, Typography } from "@mui/material";

import { ReactComponent as PaperPlaneIcon } from "../../assets/paper-plane.svg";

import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";

export const SubmissionScreen = (): JSX.Element => {
  const { successfulSubmission } = useContext(FinancialConvoContext);
  if (successfulSubmission) {
    return <SuccessfulSubmission />;
  } else {
    return <FailedSubmission />;
  }
};

const SuccessfulSubmission = (): JSX.Element => {
  const { flow } = useAuthContext()
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  const handleComplete = async (): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    window.location.href = `${LARAVEL_APP_HOST}/home`;
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper centerContent={true}>
      <>
        <Box
          sx={{
            my: 2,
            textAlign: "center",
          }}
        >
          <PaperPlaneIcon />
        </Box>
        <Box
          sx={{
            my: 2,
            textAlign: "center",
          }}
        >
          <Typography component="h2" variant="h3">
            Success! We've sent your info to an advisor
          </Typography>
          <Typography
            variant="body1"
            sx={{
              my: 2,
              mx: "auto",
              width: "80%",
              maxWidth: 480,
            }}
          >
            Someone will be reaching out to you soon. Be on the lookout for an
            email or phone call over the next few days.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            my: "1.5rem",
          }}
        >
          <Button
            variant="contained"
            onClick={handleComplete}
            sx={{
              mb: "1rem",
            }}
          >
            Got it, thanks!
          </Button>
        </Box>
      </>
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};

const FailedSubmission = (): JSX.Element => {
  const navigate = useNavigate();

  const { flow } = useAuthContext()
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  enum Outcomes {
    tryAgain = "Let's try again",
    returnToHome = "Return to Home - I'll try again later.",
  }

  const handleComplete = async (outcome: Outcomes): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    if (outcome == Outcomes.returnToHome) {
      window.location.href = `${LARAVEL_APP_HOST}/home`;
    } else if (outcome == Outcomes.tryAgain) {
      navigate(-1);
    } else {
      throw "Unsupported outcome";
    }
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper centerContent={true}>
      <>
        <Box
          sx={{
            my: 2,
            textAlign: "center",
          }}
        >
          <PaperPlaneIcon />
        </Box>
        <Box
          sx={{
            my: 2,
            textAlign: "center",
          }}
        >
          <Typography component="h2" variant="h3">
            Hmm... Something went wrong.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              my: 2,
              mx: "auto",
              width: "80%",
              maxWidth: 480,
            }}
          >
            Looks like your answers were saved, but we had a problem with
            submitting the request.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            my: "1.5rem",
          }}
        >
          <Button
            variant="contained"
            onClick={() => {
              handleComplete(Outcomes.tryAgain);
            }}
            sx={{
              mb: "1rem",
            }}
          >
            {Outcomes.tryAgain}
          </Button>
          <Button
            variant="text"
            onClick={() => {
              handleComplete(Outcomes.returnToHome);
            }}
            sx={{
              mb: "1rem",
            }}
          >
            {Outcomes.returnToHome}
          </Button>
        </Box>
      </>
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
